/* ============================================================
   layoutMargin — margin-card packing + SVG connectors

   Ported VERBATIM from prototype/reader.html section 4. Anchors
   each card at the vertical middle of its highlight, packs around
   an active "pivot" card (push-up above, push-down below), and
   draws the elbow connector paths. Pure DOM helper — takes live
   elements, mutates `card.style.top`, repopulates the SVG.
   ============================================================ */

export interface LayoutItem {
  id: string
  cardEl: HTMLElement
  anchorEl: HTMLElement
  /** When true, the card is positioned beside `anchorEl` for a vertical home but
   *  NO connector is drawn — used for resolved comments whose quoted passage is
   *  gone from this revision, so they no longer reference any specific words. */
  noConnector?: boolean
  /** Connector stroke color — the comment's accent (author hue / agent /
   *  default yellow), so the leader line matches its highlight. A CSS color or a
   *  `var(--token)` (resolved against the cascade). Defaults to the yellow bar. */
  color?: string
}

interface LayoutArgs {
  marginArea: HTMLElement
  connectorsSvg: SVGSVGElement
  comments: LayoutItem[]
  activeId?: string | null
  padding?: number
}

export function layoutMargin({
  marginArea,
  connectorsSvg,
  comments,
  activeId = null,
  padding = 8,
}: LayoutArgs): void {
  const clearSvg = (svg: SVGSVGElement) => {
    while (svg.firstChild) svg.removeChild(svg.firstChild)
  }
  clearSvg(connectorsSvg)

  if (!comments || comments.length === 0) return

  const marginRect = marginArea.getBoundingClientRect()
  connectorsSvg.setAttribute('width', String(marginRect.width))
  connectorsSvg.setAttribute('height', String(marginRect.height))

  const items = comments.map((c) => {
    const anchorRect = c.anchorEl.getBoundingClientRect()
    const cardRect = c.cardEl.getBoundingClientRect()
    const idealTop = anchorRect.top - marginRect.top
    // Point connector at the vertical middle of the highlighted word.
    const anchorY = anchorRect.top - marginRect.top + anchorRect.height / 2
    // Anchor connectors at the left edge of the margin column.
    const anchorX = 0
    const height = c.cardEl.offsetHeight || cardRect.height
    return {
      id: c.id,
      cardEl: c.cardEl,
      idealTop,
      anchorX,
      anchorY,
      height,
      top: 0,
      noConnector: !!c.noConnector,
      color: c.color,
    }
  })

  items.sort((a, b) => a.idealTop - b.idealTop)

  let pivot = 0
  if (activeId) {
    const found = items.findIndex((it) => it.id === activeId)
    if (found !== -1) pivot = found
  }

  items[pivot].top = items[pivot].idealTop

  for (let i = pivot - 1; i >= 0; i--) {
    const below = items[i + 1]
    const maxTop = below.top - padding - items[i].height
    items[i].top = Math.min(items[i].idealTop, maxTop)
  }
  for (let i = pivot + 1; i < items.length; i++) {
    const above = items[i - 1]
    const minTop = above.top + above.height + padding
    items[i].top = Math.max(items[i].idealTop, minTop)
  }

  for (const it of items) {
    it.cardEl.style.top = it.top + 'px'
  }

  const SVG_NS = 'http://www.w3.org/2000/svg'
  for (const it of items) {
    // A card with no live highlight references nothing in the doc — give it a
    // home in the column but don't draw a leader line to a passage that's gone.
    if (it.noConnector) continue
    const cardRect = it.cardEl.getBoundingClientRect()
    const cardX = cardRect.left - marginRect.left
    const cardY = it.top + Math.min(it.height / 2, 16)
    const midX = it.anchorX + (cardX - it.anchorX) / 2

    const d = `M ${it.anchorX},${it.anchorY} L ${midX},${it.anchorY} L ${midX},${cardY} L ${cardX},${cardY}`

    const path = document.createElementNS(SVG_NS, 'path')
    path.setAttribute('d', d)
    path.setAttribute('fill', 'none')
    const isActive = it.id === activeId
    // The connector IS the highlight's color reaching back to the note — it
    // follows the comment's own accent (author hue / agent / carried / yellow),
    // not a fixed neutral. Set via style (not the attribute) so a `var(--token)`
    // resolves against the cascade. Resting reads quieter via opacity/width.
    path.style.stroke = it.color || 'var(--highlight-bd)'
    path.setAttribute('stroke-width', isActive ? '2.5' : '1.75')
    path.setAttribute('stroke-linecap', 'round')
    path.setAttribute('stroke-linejoin', 'round')
    path.setAttribute('opacity', isActive ? '1' : '0.6')
    connectorsSvg.appendChild(path)
  }
}
